import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Chip,
  Button,
  Pagination,
  Stack,
  InputAdornment,
  CircularProgress,
  Paper,
  Divider,
  Collapse,
} from '@mui/material';
import {
  Search,
  LocationOn,
  Clear,
  FilterList,
  ExpandMore,
  ExpandLess,
  Home,
  Add,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApiGet } from '../../utils/httpClientTs';
import { getBedAndBreakfasts } from '../../endpoints/bedandbreakfast';
import {
  BedAndBreakfastMinimal,
  BnBSearchParams,
} from '../../types/bedandbreakfast';
import SPDSelector from '../../Components/SPDSelector';
import ChapterSelector from '../../Components/ChapterSelector';
import FeatureDescription from '../../Components/FeatureDescription';
import { roles } from '../../utils/variables';
import AlertItem from '../../Components/AlertItem';
import { BNB_DESCRIPTION } from '../../constants/featureDescriptions';

const BnBList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useState<BnBSearchParams>({
    page: 1,
    per_page: 12,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [spd, setSpd] = useState('');
  const [selectedChapter, setSelectedChapter] = useState<any>(null);

  const {
    data: bnbData,
    isLoading,
    error,
    refetch,
  } = useApiGet(
    ['bed-and-breakfasts', searchParams],
    () => getBedAndBreakfasts(searchParams),
    {
      enabled: true,
      retry: 1,
    }
  );

  const handleSearch = () => {
    setSearchParams((prev) => ({
      ...prev,
      search: searchTerm || undefined,
      page: 1,
    }));
  };

  const handleFilterChange = (key: keyof BnBSearchParams, value: any) => {
    setSearchParams((prev) => ({
      ...prev,
      [key]: value,
      page: 1,
    }));
  };

  const handleSpdChange = (value: string) => {
    setSpd(value);
    // Reset chapter selection when SPD changes
    setSelectedChapter(null);
    handleFilterChange('spd_id', value);
    handleFilterChange('local_chapter_id', undefined);
  };

  const handleChapterChange = (chapter: any) => {
    setSelectedChapter(chapter);
    handleFilterChange('local_chapter_id', chapter?.id || undefined);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setSearchParams((prev) => ({
      ...prev,
      page,
    }));
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSpd('');
    setSelectedChapter(null);
    setSearchParams({
      page: 1,
      per_page: 12,
    });
  };

  const handleBnBClick = (bnb: BedAndBreakfastMinimal) => {
    navigate(`/bnb/${bnb.id}`);
  };

  const canCreateBnB = (): boolean => {
    const userRole = localStorage.getItem('role');
    const allowedRoles = [roles.Admin, roles.Support, roles.Communications];
    return allowedRoles.includes(userRole || '');
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 2 }}>
          <AlertItem
            severity="error"
            description={`Error loading bed and breakfasts: ${error.message}`}
            onClose={() => {}}
            keepOpen={true}
          />
        </Box>
        <Button variant="contained" onClick={() => refetch()}>
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header: title and button on one row */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h4" component="h1">
          Bed & Breakfast Directory
        </Typography>
        {canCreateBnB() && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/bnb/create')}
            sx={{ height: 'fit-content' }}
          >
            Create New B&B
          </Button>
        )}
      </Box>

      <FeatureDescription
        shortText={BNB_DESCRIPTION.short}
        longText={BNB_DESCRIPTION.long}
      />

      <Box sx={{ mb: 3 }} />

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Search by name, city, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      onClick={() => {
                        setSearchTerm('');
                        handleSearch();
                      }}
                    >
                      <Clear />
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              variant="contained"
              onClick={handleSearch}
              startIcon={<Search />}
              sx={{ minWidth: { xs: '100%', md: 'auto', marginRight: 3 } }}
            >
              Search
            </Button>
            <Button
              variant="outlined"
              onClick={() => setShowFilters(!showFilters)}
              startIcon={<FilterList />}
              endIcon={showFilters ? <ExpandLess /> : <ExpandMore />}
              sx={{ minWidth: { xs: '100%', md: 'auto', marginRight: 3 } }}
            >
              Filters
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              startIcon={<Clear />}
              sx={{ minWidth: { xs: '100%', md: 'auto' } }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>

        {/* Collapsible Filters */}
        <Collapse in={showFilters}>
          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <SPDSelector
                  spd={spd}
                  handleSpdSelect={handleSpdChange}
                  reportId="bnb-list"
                  all={true}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <ChapterSelector
                  chapter={selectedChapter?.id}
                  handleChapterSelect={handleChapterChange}
                  spd={spd}
                  reportId="bnb-list"
                  label="Local Chapter"
                  objectReturned={true}
                  all={true}
                />
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Results */}
      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">
              {bnbData?.pagination.total_count || 0} bed and breakfast
              {bnbData?.pagination.total_count !== 1 ? 's' : ''} found
            </Typography>
          </Box>

          {bnbData?.data.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No bed and breakfasts found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search criteria or filters
              </Typography>
            </Paper>
          ) : (
            <>
              <Grid container spacing={3}>
                {bnbData?.data.map((bnb) => (
                  <Grid item xs={12} md={6} lg={4} key={bnb.id}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4,
                        },
                      }}
                      onClick={() => handleBnBClick(bnb)}
                    >
                      {/* Header with home icon */}
                      <Box
                        sx={{
                          backgroundColor: 'primary.veryLight',
                          p: 3,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                        }}
                      >
                        <Home
                          sx={{
                            fontSize: 60,
                            color: 'primary.main',
                          }}
                        />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography
                            variant="h5"
                            component="h2"
                            fontWeight="600"
                            gutterBottom
                            color="text.primary"
                          >
                            {bnb.name}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <LocationOn
                              sx={{ fontSize: 18, color: 'text.secondary' }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {bnb.local_chapter_name ||
                                'Location details available'}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      <CardContent
                        sx={{
                          flexGrow: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          p: 2.5,
                        }}
                      >
                        {bnb.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mb: 2,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              lineHeight: 1.6,
                            }}
                          >
                            {bnb.description}
                          </Typography>
                        )}

                        {!bnb.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2, fontStyle: 'italic' }}
                          >
                            View details to learn more about this property
                          </Typography>
                        )}

                        {bnb.amenities && bnb.amenities.length > 0 && (
                          <Box mb={2}>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              flexWrap="wrap"
                              useFlexGap
                            >
                              {bnb.amenities
                                .slice(0, 3)
                                .map((amenity, index) => (
                                  <Chip
                                    key={index}
                                    label={amenity}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.75rem' }}
                                  />
                                ))}
                              {bnb.amenities.length > 3 && (
                                <Chip
                                  label={`+${bnb.amenities.length - 3} more`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.75rem' }}
                                />
                              )}
                            </Stack>
                          </Box>
                        )}

                        <Box mt="auto">
                          <Divider sx={{ mb: 1 }} />
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography variant="body2" color="text.secondary">
                              {bnb.local_chapter_name}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="primary"
                              fontWeight="medium"
                            >
                              View Details →
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Pagination */}
              {bnbData &&
                bnbData.pagination.total_count >
                  bnbData.pagination.per_page && (
                  <Box display="flex" justifyContent="center" mt={4}>
                    <Pagination
                      count={bnbData.pagination.total_pages}
                      page={bnbData.pagination.page}
                      onChange={handlePageChange}
                      color="primary"
                      size="large"
                    />
                  </Box>
                )}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default BnBList;
